import { defineComponent } from 'vue';
import type { PresetOptionType } from '@/types/global-style';
import InitProcessButton from '@/mixins/processButton';
import { actions, mutations } from '@modules/editor-sidebar';
import { mapActions, mapState, mapMutations } from 'vuex';
import type { PresetOption } from '@/types/global-style';
import { presetOptionTypes } from '../../constants/styleConstant';
import type { PresetOptionParams } from '@/types/global-style-http';
import { mutations as mutaIframe } from '@/store/iframe/types';
import emitter from '@/emitter';
import { applyPreset } from '@/utils/apply-preset';
import store from '@/app/store/store';
import { actions as commonActions } from '@/store/common/types';
import { EDITOR_EVENT } from '@/app/editor/config/constants/trackEventIds';
import { mutations as mutaApp } from '@/store/app/types';

export default defineComponent({
  mixins: [InitProcessButton],
  computed: {
    ...mapState('sidebar', {
      currentPreset: (state) => (state as any).currentPreset,
      currentPresetOptionType: (state) => (state as any).currentPresetOptionType,
      linkedPresetId: (state) => (state as any).linkedPresetId,
    }),
    ...mapState('app', {
      shopMeta: 'shopMeta',
    }),
    processStatus() {
      return this.btnStatus;
    },
    obGlobalStyle() {
      return this.shopMeta.onboardingGlobalStyle;
    },
  },
  methods: {
    ...mapActions('sidebar', {
      savePresetOptions: actions.SAVE_PRESET_OPTIONS,
      updateLinkedPresetList: actions.UPDATE_LINKED_PRESET_LIST,
    }),
    ...mapActions('app', {
      updateShopMeta: 'updateShopMeta',
    }),
    ...mapMutations('sidebar', {
      setCurrentPresetOptions: mutations.SET_CURRENT_PRESET_OPTIONS,
      setCurrentPreset: mutations.SET_CURRENT_PRESET,
      setPresetToPresetList: mutations.SET_PERSET_TO_PRESET_LIST,
      setCurrentPresetInPage: mutations.SET_CURRENT_PRESET_IN_PAGE,
      setIsPublishPreset: mutations.SET_IS_PUBLISH_PRESET,
      setOldLinkedPresetId: mutations.SET_OLD_LINKED_PRESET_ID,
    }),
    ...mapMutations('app', {
      updatePageStatusPublishedGs: mutaApp.UPDATE_PAGE_STATUS_PUBLISHED_GS,
      updatePagePresetId: mutaApp.UPDATE_PAGE_PRESET_ID,
    }),
    ...mapMutations('iframe', {
      ALERT: mutaIframe.ALERT,
      alert: 'alert',
    }),
    ...mapActions('common', {
      trackEvent: commonActions.TRACK_EVENT,
    }),
    prepareSaveData(): PresetOption[] {
      return (this as any).$refs?.component?.currentPresetOptions;
    },
    async handleSaveData(): Promise<any> {
      this.trackEvent({
        eventId: EDITOR_EVENT.GS_UPDATE_BUTTON_CLICKED,
        properties: { type: this.currentPresetOptionType },
      });

      if (this.checkInvalidData()) return;

      const presetOptions = this.prepareSaveData();
      // this.activeLoading();
      const params: PresetOptionParams = {
        presetId: this.currentPreset.id as number,
        type: this.currentPresetOptionType as PresetOptionType,
        destroy: this.currentPreset.destroy as boolean,
        presetOptions: presetOptions,
        cssStr: '' as string,
      };

      return this.savePresetOptions(params)
        .then((response: any) => {
          setTimeout(() => {
            // this.activeSuccess();
            this.updatePreset(response);
            this.ALERT({
              show: true,
              title: '',
              message: 'Successfully <span class="typo-semibold">saved</span> changes',
              typeAlert: 'success',
            });
          }, 200);
        })
        .catch((error: any) => {
          setTimeout(() => {
            // this.activeError();
            this.ALERT({
              show: true,
              // title: "Failed to save changes.",
              // message: "error.response.data.message",
              title: '',
              message:
                'Failed to <span class="typo-semibold">save</span> changes. Check your Internet connection and try again later.',
              typeAlert: 'error',
            });
          }, 200);
        })
        .finally(() => {
          setTimeout(() => {
            // this.resetStatus();
          }, 500);
        });
    },
    checkInvalidData(): boolean {
      return !(this as any).$refs?.component?.isReadyToSave;
    },
    updateCurrentPresetOptions(currentPreset: any) {
      if (!currentPreset) {
        currentPreset = this.currentPreset;
      }
      if (this.currentPresetOptionType === presetOptionTypes.spacing) {
        this.setCurrentPresetOptions(currentPreset.presetOptions?.spacings);
      }
      if (this.currentPresetOptionType === presetOptionTypes.color) {
        this.setCurrentPresetOptions(currentPreset.presetOptions?.colors);
        emitter.emit('cancelColor', currentPreset.presetOptions?.colors);
      }
      if (this.currentPresetOptionType === presetOptionTypes.corner) {
        this.setCurrentPresetOptions(currentPreset.presetOptions?.corners);
      }
      if (this.currentPresetOptionType === presetOptionTypes.border) {
        this.setCurrentPresetOptions(currentPreset.presetOptions?.borders);
      }
      if (this.currentPresetOptionType === presetOptionTypes.button) {
        this.setCurrentPresetOptions(currentPreset.presetOptions.buttons);
      }
      if (this.currentPresetOptionType === presetOptionTypes.text) {
        this.setCurrentPresetOptions(currentPreset.presetOptions?.texts);
      }
    },
    handleCancel(): void {
      this.trackEvent({
        eventId: EDITOR_EVENT.GS_DISCARD_BUTTON_CLICKED,
        properties: { type: this.currentPresetOptionType },
      });
      this.updateCurrentPresetOptions(undefined);
    },
    updatePreset(preset: any): void {
      if (!preset) return;

      this.setCurrentPreset(preset);
      this.setPresetToPresetList(preset);

      this.updateCurrentPresetOptions(preset);
      if (+this.linkedPresetId === +preset.id) {
        this.setCurrentPresetInPage(preset);
        store.setValue('currentPresetInPage', preset);
        applyPreset(preset, null);
      }

      emitter.emit('hook:changeButtonSetting');
    },
    async applyPresetThisPage(pageID: number, preset: any) {
      this.alert({
        show: true,
        title: 'Applying',
        message: 'preset to current page',
        typeAlert: 'info',
        delay: 'infinity',
      });

      try {
        const oldLinkedPresetId = this.linkedPresetId;
        const res = await (this as any).applyPreset({
          pageId: pageID,
          presetId: preset.id,
        });

        if (res.status === 'success') {
          if (!preset) return false;

          this.alert({
            show: true,
            title: 'Successfully',
            message: 'apply preset from the current page.',
            typeAlert: 'success',
          });

          const shopMeta = this.shopMeta;
          shopMeta['onboardingGlobalStyle']['applyPreset'] = true;

          this.updateLinkedPresetList(preset);
          this.updatePagePresetId(+preset.id);
          this.setCurrentPresetInPage(preset);
          store.setValue('currentPresetInPage', preset);
          this.setOldLinkedPresetId(oldLinkedPresetId);
          applyPreset(preset, { isOnApplyPreset: true });
          return true;
        } else {
          let title = 'Apply failed.';
          let message = res.message;
          if (res.errorCode === 'GS00006') {
            title = '';
            message = this.msgAlreadyDelete(preset);
          }
          this.alert({
            show: true,
            title: title,
            message: message,
            typeAlert: 'error',
          });
          return false;
        }
      } catch (error) {
        this.alert({
          show: true,
          title: 'Failed',
          message: ' to apply preset. Check your internet connection and try again later.',
          typeAlert: 'error',
        });
        return false;
      }
    },
    msgAlreadyDelete(preset: any) {
      const presetName = preset?.name || 'Preset';
      return `${presetName} is already <b>deleted</b>. Please save your progress and reload the editor to see the changes.`;
    },
  },
});
